'use client';

import { ENGLISH_IQ_EVENTS } from '@repo/analytics';
import type { IEnglishIqDemoV2 } from '@repo/sdk';
import { trackEvent } from '@repo/web-chrome/lib/analytics';
import { useEffect, useRef, useState } from 'react';
import type { EnglishIqMessagesType } from '../../english-iq-messages-type';

const DEMO_MEDIA_STALL_TIMEOUT_MS = 8_000;

interface IEnglishIqV2DemoProps {
  active: boolean;
  canFinish: boolean;
  demo: IEnglishIqDemoV2 | null;
  firstClipRef: React.RefObject<HTMLVideoElement | null>;
  messages: EnglishIqMessagesType['v2']['demo'];
  onFinish: () => void;
}

export function EnglishIqV2Demo({
  active,
  canFinish,
  demo,
  firstClipRef,
  messages,
  onFinish,
}: IEnglishIqV2DemoProps): React.JSX.Element {
  const trackedRef = useRef(false);
  const headingRef = useRef<HTMLHeadingElement>(null);
  const secondClipRef = useRef<HTMLVideoElement>(null);
  const [completedClipIds, setCompletedClipIds] = useState<number[]>([]);
  const [failedClipIds, setFailedClipIds] = useState<number[]>([]);
  const [forcedFallback, setForcedFallback] = useState(false);
  const dataSaver =
    typeof navigator !== 'undefined' &&
    Boolean(
      (navigator as Navigator & { connection?: { saveData?: boolean } }).connection?.saveData,
    );
  const useTextFallback = shouldUseTextFallback(demo, dataSaver, failedClipIds, forcedFallback);
  const clipsComplete = areDemoClipsComplete(demo, completedClipIds);
  const revealTeaching = useTextFallback || clipsComplete;

  useEffect(() => {
    if (active && !trackedRef.current) {
      trackedRef.current = true;
      trackEvent(ENGLISH_IQ_EVENTS.demoStart, { surface: 'web_v2' });
    }
    if (active) {
      headingRef.current?.focus();
    }
  }, [active]);

  useEffect(() => {
    const completedClipCount = completedClipIds.length;
    if (!active || !demo || useTextFallback || completedClipCount >= demo.clips.length) {
      return;
    }
    const timeoutId = window.setTimeout(() => setForcedFallback(true), DEMO_MEDIA_STALL_TIMEOUT_MS);
    return () => window.clearTimeout(timeoutId);
  }, [active, completedClipIds.length, demo, useTextFallback]);

  const finish = (): void => {
    trackDemoComplete(useTextFallback);
    onFinish();
  };
  const completeClip = (clipId: number, index: number): void => {
    setCompletedClipIds((current) => (current.includes(clipId) ? current : [...current, clipId]));
    if (index === 0) {
      void secondClipRef.current?.play().catch(() => undefined);
    }
  };
  return (
    <DemoView
      canFinish={canFinish}
      demo={demo}
      finish={finish}
      headingRef={headingRef}
      media={{
        active,
        demo,
        firstClipRef,
        messages,
        onComplete: completeClip,
        onError: (clipId) => setFailedClipIds((current) => [...current, clipId]),
        secondClipRef,
        useTextFallback,
      }}
      messages={messages}
      onForceFallback={() => setForcedFallback(true)}
      revealTeaching={revealTeaching}
    />
  );
}

interface IDemoViewProps {
  canFinish: boolean;
  demo: IEnglishIqDemoV2 | null;
  finish: () => void;
  headingRef: React.RefObject<HTMLHeadingElement | null>;
  media: IDemoMediaProps;
  messages: EnglishIqMessagesType['v2']['demo'];
  onForceFallback: () => void;
  revealTeaching: boolean;
}

function DemoView(props: IDemoViewProps): React.JSX.Element {
  return (
    <main
      aria-live="polite"
      className="mx-auto grid min-h-[100svh] max-w-6xl content-center px-4 py-8 sm:px-8"
    >
      <p className="text-xs font-bold uppercase tracking-[0.22em] text-[#ff9b50]">
        {props.messages.eyebrow}
      </p>
      <h2
        className="mt-3 max-w-3xl text-balance text-4xl font-semibold tracking-[-0.04em] outline-none sm:text-6xl"
        ref={props.headingRef}
        tabIndex={-1}
      >
        {props.messages.title}
      </h2>
      <DemoMedia {...props.media} />
      {!props.revealTeaching ? (
        <button
          className="mt-4 min-h-11 justify-self-start text-sm font-bold text-[#ffb36b] underline-offset-4 hover:underline"
          onClick={props.onForceFallback}
          type="button"
        >
          {props.messages.skipMedia}
        </button>
      ) : null}
      {props.demo && props.revealTeaching ? (
        <div className="mt-7 grid gap-4 rounded-3xl border border-[#ff7a1a]/25 bg-[#ff7a1a]/10 p-6 sm:grid-cols-2 sm:p-8">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#ffb36b]">
              {props.messages.phrase}
            </p>
            <p className="mt-2 text-3xl font-semibold" dir="ltr">
              {props.demo.challenge}
            </p>
          </div>
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#ffb36b]">
              {props.messages.meaning}
            </p>
            <p className="mt-2 text-lg leading-7 text-[#fff8e9]/80">{props.demo.meaning}</p>
          </div>
        </div>
      ) : null}
      <button
        className="mt-8 min-h-14 w-full rounded-full bg-[#ff7a1a] px-8 font-extrabold text-[#160c06] sm:w-auto"
        disabled={!props.canFinish || !props.revealTeaching}
        onClick={props.finish}
        type="button"
      >
        {props.canFinish && props.revealTeaching ? props.messages.continue : props.messages.eyebrow}
      </button>
    </main>
  );
}

function trackDemoComplete(fallback: boolean): void {
  trackEvent(ENGLISH_IQ_EVENTS.demoComplete, { fallback, surface: 'web_v2' });
}

interface IDemoMediaProps {
  active: boolean;
  demo: IEnglishIqDemoV2 | null;
  firstClipRef: React.RefObject<HTMLVideoElement | null>;
  messages: EnglishIqMessagesType['v2']['demo'];
  onComplete: (clipId: number, index: number) => void;
  onError: (clipId: number) => void;
  secondClipRef: React.RefObject<HTMLVideoElement | null>;
  useTextFallback: boolean;
}

function DemoMedia(props: IDemoMediaProps): React.JSX.Element {
  if (!props.demo || props.useTextFallback) {
    return (
      <div className="mt-8 rounded-3xl border border-[#f7edda]/15 bg-[#f7edda]/5 p-6 text-lg leading-8 text-[#f7edda]/75">
        {props.messages.fallback}
      </div>
    );
  }
  return (
    <div className="mt-8 grid gap-4 md:grid-cols-2">
      {props.demo.clips.map((clip, index) => {
        const mediaProps = {
          'aria-label': props.messages.clip.replace('{index}', String(index + 1)),
          autoPlay: props.active && index === 0,
          className: 'aspect-video w-full object-cover',
          controls: true,
          onEnded: () => props.onComplete(clip.id, index),
          onError: () => props.onError(clip.id),
          playsInline: true,
          preload: 'auto' as const,
          src: clip.url,
        };
        return (
          <figure
            className="overflow-hidden rounded-3xl border border-[#f7edda]/15 bg-black"
            key={clip.id}
          >
            <video {...mediaProps} ref={index === 0 ? props.firstClipRef : props.secondClipRef} />
          </figure>
        );
      })}
    </div>
  );
}

function shouldUseTextFallback(
  demo: IEnglishIqDemoV2 | null,
  dataSaver: boolean,
  failedClipIds: number[],
  forcedFallback: boolean,
): boolean {
  return !demo || dataSaver || failedClipIds.length > 0 || forcedFallback;
}

function areDemoClipsComplete(demo: IEnglishIqDemoV2 | null, completedClipIds: number[]): boolean {
  return demo ? completedClipIds.length === demo.clips.length : false;
}
